import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLayout } from '../../contexts/LayoutContext';
import { 
  LayoutDashboard, Users, Building2, School, Calendar, 
  FileText, UserCog, CheckSquare, 
  FileBarChart, Package, Settings as SettingsIcon, Activity
} from 'lucide-react';
import { cn } from '../../utils/cn';

const Sidebar = () => {
  const { user: authUser, hasPermission } = useAuth();
  const { sidebarOpen, closeSidebar } = useLayout();
  const location = useLocation();

  const menuItems = [
    { 
      path: '/dashboard', 
      icon: LayoutDashboard, 
      label: 'Dashboard', 
      permissions: ['dashboard:view'] 
    },
    { 
      path: '/dashboard/attendance', 
      icon: CheckSquare, 
      label: 'Staff Attendance', 
      permissions: ['attendance:read', 'attendance:clockin', 'attendance:clockout', 'attendance:view'] 
    },
    { 
      path: '/dashboard/meal-requests', 
      icon: Package, 
      label: 'Meal Requests', 
      permissions: ['meal:request:read', 'meal:request:create', 'meal:request:update'] 
    },
    { 
      path: '/dashboard/branches', 
      icon: Building2, 
      label: 'Branches', 
      permissions: ['branch:read', 'branch:create', 'branch:update', 'branch:delete'] 
    },
    { 
      path: '/dashboard/schools', 
      icon: School, 
      label: 'Schools', 
      permissions: ['school:read', 'school:create', 'school:update', 'school:delete'] 
    },
    { 
      path: '/dashboard/tasks', 
      icon: FileText, 
      label: 'Tasks', 
      permissions: ['task:read', 'task:create', 'task:update', 'task:delete'] 
    },
    { 
      path: '/dashboard/calendar', 
      icon: Calendar, 
      label: 'Calendar', 
      permissions: ['calendar:read', 'calendar:view'] 
    },
    { 
      path: '/dashboard/reports', 
      icon: FileBarChart, 
      label: 'Reports', 
      permissions: ['report:view', 'report:generate'] 
    },
    { 
      path: '/dashboard/activities', 
      icon: Activity, 
      label: 'Activities', 
      permissions: ['activity:view'] 
    },
    { 
      path: '/dashboard/users', 
      icon: Users, 
      label: 'Users', 
      permissions: ['user:read', 'user:create', 'user:update', 'user:delete'] 
    },
    { 
      path: '/dashboard/roles', 
      icon: UserCog, 
      label: 'Roles', 
      permissions: ['role:view', 'role:manage'] 
    },
    { 
      path: '/dashboard/settings', 
      icon: SettingsIcon, 
      label: 'Settings', 
      permissions: ['settings:read', 'settings:update'] 
    },
  ];

  const hasAccess = (permissions) => {
    return permissions.some(permission => {
      if (permission === 'dashboard:view') return true;
      return hasPermission(permission);
    });
  };

  return (
    <>
      {/* Sidebar */}
      <aside className={cn(
        'fixed inset-y-0 left-0 z-20 w-64 bg-white border-r border-gray-200 pt-16 transform transition-transform duration-300 ease-in-out',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        'lg:translate-x-0'
      )}>
        <nav className="p-4 space-y-1 h-full overflow-y-auto">
          {menuItems.filter(item => hasAccess(item.permissions)).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeSidebar}
                className={cn(
                  'flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 group',
                  isActive
                    ? 'bg-orange-500 text-white shadow-lg'
                    : 'text-gray-700 hover:bg-orange-50 hover:text-orange-500'
                )}
              >
                <Icon 
                  size={20} 
                  className={cn(
                    'transition-colors duration-200',
                    isActive ? 'text-white' : 'text-gray-500 group-hover:text-orange-500'
                  )} 
                />
                <span
                  className={cn(
                    'font-medium',
                    isActive ? 'text-white' : 'text-gray-700 group-hover:text-orange-500'
                  )}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </nav>
      </aside>
    </>
  );
};

export { Sidebar };
